/**
 * Derived relations — pure (non-reactive) core, safe for workers and
 * non-solid code paths (exports, share subtree collection).
 *
 * The reactive counterpart lives in ./derived-relations.ts — KEEP THE
 * LINKAGE CHAIN IN SYNC between the two:
 *   root —structuredTranscript/sourceEntity→ trx
 *   trx  —transcription/recording (canonical) | transcription/sourceEntity (daemon legacy)→ anchor
 * and the gate: a block with ANY live parent relation is NOT derived
 * (real relations trump).
 */

import type { EntityID } from '@wovin/core/applog'
import type { ThreadOnlyCurrentNoDeleted } from '@wovin/core/thread'
import { queryAndMap } from '@wovin/core'

/**
 * One-shot derived kids of an anchor entity, sorted by processedAt.
 * Mirrors the reactive provider in ./derived-relations.ts.
 */
export function getDerivedKids(thread: ThreadOnlyCurrentNoDeleted, anchorID: EntityID): EntityID[] {
	const roots = queryAndMap(
		thread,
		[{ en: '?root', at: 'structuredTranscript/sourceEntity', vl: '?trx' }],
		{ root: 'root', trx: 'trx' } as const,
	) as Array<{ root: EntityID; trx: EntityID }>

	const kids: { id: EntityID; processedAt: string }[] = []
	for (const { root, trx } of roots) {
		if (root === anchorID) continue
		const anchor = queryAndMap(thread, [{ en: trx, at: 'transcription/recording', vl: '?a' }], 'a')[0]
			?? queryAndMap(thread, [{ en: trx, at: 'transcription/sourceEntity', vl: '?a' }], 'a')[0]
		if (anchor !== anchorID) continue
		// real relations trump — any live parent relation excludes the block from derivation
		const hasParent = queryAndMap(thread, [{ en: '?rel', at: 'relation/block', vl: root }], 'rel').length > 0
		if (hasParent) continue
		const processedAt = queryAndMap(thread, [{ en: root, at: 'structuredTranscript/processedAt', vl: '?p' }], 'p')[0]
		kids.push({ id: root, processedAt: String(processedAt ?? '') })
	}
	kids.sort((a, b) => a.processedAt.localeCompare(b.processedAt) || a.id.localeCompare(b.id))
	return kids.map(k => k.id)
}

/**
 * Entities a derived kid's placement depends on (currently: its transcription
 * entities). Share/export subtree collection must include their applogs, or
 * the receiving side can't resolve the anchor and the block arrives as an
 * invisible orphan.
 */
export function getDerivedLinkageEntities(thread: ThreadOnlyCurrentNoDeleted, derivedKidID: EntityID): EntityID[] {
	return queryAndMap(thread, [{ en: derivedKidID, at: 'structuredTranscript/sourceEntity', vl: '?trx' }], 'trx') as EntityID[]
}
